import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { baseAPI } from '../../../config';
import Pagination from '../../../components/Pagination';
import AIAgentLogBatchDetail from './AIAgentLogBatchDetail';

const EVENT_LABELS = {
  processing_started: 'Inicio',
  processing_completed: 'Completado',
  processing_error: 'Error',
  rate_limited: 'Limitado',
  max_retries_reached: 'Max reintentos',
  circuit_breaker_open: 'Circuit breaker',
  ai_responded: 'Respuesta IA',
  ai_called: 'Llamada IA',
  action_executed: 'Accion',
  context_gathered: 'Contexto',
  prompt_resolved: 'Prompt',
  follow_up_created: 'Follow-up creado',
  sequence_stopped: 'Secuencia parada',
};

const EVENT_COLORS = {
  processing_completed: 'bg-green-100 text-green-700',
  processing_error: 'bg-red-100 text-red-700',
  rate_limited: 'bg-yellow-100 text-yellow-700',
  max_retries_reached: 'bg-red-100 text-red-700',
  circuit_breaker_open: 'bg-red-100 text-red-700',
  ai_responded: 'bg-blue-100 text-blue-700',
  ai_called: 'bg-blue-100 text-blue-700',
  action_executed: 'bg-indigo-100 text-indigo-700',
  processing_started: 'bg-slate-100 text-slate-600',
  context_gathered: 'bg-slate-100 text-slate-600',
  prompt_resolved: 'bg-slate-100 text-slate-600',
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return (
    d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' }) +
    ' ' +
    d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  );
};

const AIAgentLogs = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    event: '',
    expedient_id: '',
    ai_provider: '',
    date_from: '',
    date_to: '',
  });
  const [expanded, setExpanded] = useState(new Set());
  const [batchDetailId, setBatchDetailId] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filters.event) params.event = filters.event;
      if (filters.expedient_id) params.expedient_id = filters.expedient_id;
      if (filters.ai_provider) params.ai_provider = filters.ai_provider;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;

      const response = await axios.get(`${baseAPI}/ai-agent-logs/tasks`, { params, headers });
      setTasks(response.data.tasks || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Error fetching grouped logs:', error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filters]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const toggleExpanded = key => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const taskFinalColor = finalEvent =>
    EVENT_COLORS[finalEvent] || 'bg-slate-100 text-slate-600';

  return (
    <div>
      {/* Filters */}
      <div className='grid grid-cols-2 md:grid-cols-5 gap-3 mb-4'>
        <select
          value={filters.event}
          onChange={e => handleFilterChange('event', e.target.value)}
          className='text-sm border border-slate-200 rounded-lg px-3 py-1.5'
        >
          <option value=''>Todos los eventos</option>
          {Object.entries(EVENT_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>

        <input
          type='text'
          value={filters.expedient_id}
          onChange={e => handleFilterChange('expedient_id', e.target.value)}
          className='text-sm border border-slate-200 rounded-lg px-3 py-1.5'
          placeholder='ID Expediente'
        />

        <select
          value={filters.ai_provider}
          onChange={e => handleFilterChange('ai_provider', e.target.value)}
          className='text-sm border border-slate-200 rounded-lg px-3 py-1.5'
        >
          <option value=''>Todos los providers</option>
          <option value='openai'>OpenAI</option>
          <option value='anthropic'>Anthropic</option>
        </select>

        <input
          type='date'
          value={filters.date_from}
          onChange={e => handleFilterChange('date_from', e.target.value)}
          className='text-sm border border-slate-200 rounded-lg px-3 py-1.5'
        />

        <input
          type='date'
          value={filters.date_to}
          onChange={e => handleFilterChange('date_to', e.target.value)}
          className='text-sm border border-slate-200 rounded-lg px-3 py-1.5'
        />
      </div>

      {loading ? (
        <div className='text-sm text-slate-500'>Cargando tareas...</div>
      ) : tasks.length === 0 ? (
        <div className='text-center py-8 text-sm text-slate-400'>Sin tareas</div>
      ) : (
        <div className='rounded-lg border border-slate-200 overflow-hidden'>
          <table className='w-full text-sm'>
            <thead>
              <tr className='bg-slate-50 border-b border-slate-200'>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500 w-8'></th>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Inicio</th>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Tarea</th>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Expediente</th>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Estado final</th>
                <th className='text-left px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Provider / Modelo</th>
                <th className='text-right px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Eventos</th>
                <th className='text-right px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Tokens</th>
                <th className='text-right px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Duracion</th>
                <th className='text-center px-4 py-2 text-xs font-semibold uppercase text-slate-500'>Batch</th>
              </tr>
            </thead>
            <tbody className='divide-y divide-slate-200'>
              {tasks.map(task => {
                const key = task._id;
                const isExpanded = expanded.has(key);
                const expedientLabel = task.expedient_id ? task.expedient_id.toString().slice(-6) : '-';
                return (
                  <React.Fragment key={key}>
                    <tr className='hover:bg-slate-50'>
                      <td className='px-4 py-2 text-center'>
                        <button
                          onClick={() => toggleExpanded(key)}
                          className='text-slate-500 font-bold w-5 h-5 inline-flex items-center justify-center'
                          aria-label={isExpanded ? 'Contraer' : 'Expandir'}
                        >
                          {isExpanded ? '−' : '+'}
                        </button>
                      </td>
                      <td className='px-4 py-2 text-xs text-slate-600 whitespace-nowrap'>
                        {formatDate(task.started_at)}
                      </td>
                      <td className='px-4 py-2 text-xs text-slate-700'>
                        {task.assignment ? (
                          <div>
                            <div className='font-medium text-slate-800 truncate max-w-[240px]' title={task.assignment.description}>
                              {task.assignment.description}
                            </div>
                            {task.assignment.task_type && (
                              <div className='text-[10px] text-slate-500'>{task.assignment.task_type}</div>
                            )}
                          </div>
                        ) : (
                          <span className='text-slate-400'>—</span>
                        )}
                      </td>
                      <td className='px-4 py-2 text-xs text-slate-600'>
                        {task.expedient_id ? (
                          <a href={`/view/${task.expedient_id}`} className='text-blue-600 hover:underline'>
                            {expedientLabel}
                          </a>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className='px-4 py-2'>
                        <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${taskFinalColor(task.final_event)}`}>
                          {EVENT_LABELS[task.final_event] || task.final_event || '-'}
                        </span>
                      </td>
                      <td className='px-4 py-2 text-xs text-slate-600'>
                        {task.ai_provider ? (
                          <>
                            <span>{task.ai_provider}</span>
                            {task.model_id && <span className='text-slate-400'> / {task.model_id}</span>}
                          </>
                        ) : (
                          '-'
                        )}
                      </td>
                      <td className='px-4 py-2 text-right text-xs text-slate-600'>{task.events_count}</td>
                      <td className='px-4 py-2 text-right text-xs'>
                        {task.tokens_used ? task.tokens_used.toLocaleString() : '-'}
                      </td>
                      <td className='px-4 py-2 text-right text-xs'>
                        {task.duration_ms ? `${(task.duration_ms / 1000).toFixed(1)}s` : '-'}
                      </td>
                      <td className='px-4 py-2 text-center'>
                        {task.batch_id && (
                          <button
                            onClick={() => setBatchDetailId(task.batch_id)}
                            className='text-xs text-blue-600 hover:text-blue-800 font-medium'
                          >
                            Ver
                          </button>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className='bg-slate-50/50'>
                        <td colSpan={10} className='px-6 py-3'>
                          <div className='rounded-lg border border-slate-200 bg-white overflow-hidden'>
                            <table className='w-full text-xs'>
                              <thead>
                                <tr className='bg-slate-50 text-slate-500'>
                                  <th className='text-left px-3 py-1.5 font-semibold uppercase'>Fecha</th>
                                  <th className='text-left px-3 py-1.5 font-semibold uppercase'>Evento</th>
                                  <th className='text-left px-3 py-1.5 font-semibold uppercase'>Provider</th>
                                  <th className='text-left px-3 py-1.5 font-semibold uppercase'>Modelo</th>
                                  <th className='text-right px-3 py-1.5 font-semibold uppercase'>Tokens</th>
                                  <th className='text-right px-3 py-1.5 font-semibold uppercase'>Duracion</th>
                                </tr>
                              </thead>
                              <tbody className='divide-y divide-slate-100'>
                                {(task.events || []).map(ev => (
                                  <tr key={ev._id}>
                                    <td className='px-3 py-1.5 text-slate-500 whitespace-nowrap'>{formatDate(ev.createdAt)}</td>
                                    <td className='px-3 py-1.5'>
                                      <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${EVENT_COLORS[ev.event] || 'bg-slate-100 text-slate-600'}`}>
                                        {EVENT_LABELS[ev.event] || ev.event}
                                      </span>
                                    </td>
                                    <td className='px-3 py-1.5 text-slate-600'>{ev.ai_provider || '-'}</td>
                                    <td className='px-3 py-1.5 text-slate-600'>{ev.model_id || '-'}</td>
                                    <td className='px-3 py-1.5 text-right'>{ev.tokens_used ? ev.tokens_used.toLocaleString() : '-'}</td>
                                    <td className='px-3 py-1.5 text-right'>{ev.duration_ms ? `${(ev.duration_ms / 1000).toFixed(1)}s` : '-'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div className='mt-4'>
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}

      <AIAgentLogBatchDetail
        batchId={batchDetailId}
        modalOpen={!!batchDetailId}
        onClose={() => setBatchDetailId(null)}
      />
    </div>
  );
};

export default AIAgentLogs;
